/**
 * Project Evidence Timeline — Iter-110c.0 (Legal Proof Engine)
 *
 * Dispute-proof chronological view of all photographic evidence captured
 * by daily logs, MAR receipts, and MIR inspections (before/during/after)
 * for a single project. Renders grouped by day, with filters and a
 * one-click "Generate Evidence Pack PDF" export for legal disputes.
 *
 * Route: /project-execution/:projectId/evidence
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  ShieldCheck, Filter, Download, MapPin, User, FileText,
  Calendar, Building, Camera, ArrowRight, Image as ImageIcon,
  Loader2,
} from "lucide-react";

const KIND_DEFS = [
  { key: "daily",      label: "تنفيذ يومي",       color: "violet" },
  { key: "mar",        label: "استلام MAR",       color: "amber"  },
  { key: "mir_before", label: "MIR قبل",          color: "rose"   },
  { key: "mir_during", label: "MIR أثناء",        color: "indigo" },
  { key: "mir_after",  label: "MIR بعد",          color: "emerald"},
];

const fmt = (n) => new Intl.NumberFormat("en-US").format(n || 0);

function KindBadge({ kind }) {
  const def = KIND_DEFS.find((k) => k.key === kind) || { label: kind, color: "slate" };
  return (
    <span className={`inline-flex items-center rounded-full bg-${def.color}-100 px-2 py-0.5 text-[10px] font-bold uppercase text-${def.color}-700`}>
      {def.label}
    </span>
  );
}

function EvidenceCard({ item }) {
  const gps = item.gps || {};
  const hasGps = gps.lat != null && gps.lng != null;
  return (
    <div data-testid={`ev-card-${item.kind}-${item.source_id}`}
         className="group rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-all">
      <a href={item.url} target="_blank" rel="noreferrer"
         className="block relative aspect-square bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <ImageIcon className="h-8 w-8 text-slate-400 group-hover:text-slate-500" />
        <div className="absolute top-2 right-2">
          <KindBadge kind={item.kind} />
        </div>
        {hasGps && (
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-emerald-600/90 px-2 py-0.5 text-[9px] font-bold text-white">
            <MapPin className="h-3 w-3" /> GPS
          </div>
        )}
      </a>
      <div className="p-2.5 text-[11px] space-y-1">
        <div className="flex items-center gap-1 text-slate-700 font-semibold">
          <Building className="h-3 w-3 text-violet-500" /> {item.area || "—"}
        </div>
        {item.source_number && (
          <div className="flex items-center gap-1 text-slate-500">
            <FileText className="h-3 w-3" /> {item.source_number}
            {item.status && <span className="text-violet-600">· {item.status}</span>}
          </div>
        )}
        {item.engineer && (
          <div className="flex items-center gap-1 text-slate-500 truncate">
            <User className="h-3 w-3" /> {item.engineer}
          </div>
        )}
        {hasGps && (
          <a href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`} target="_blank" rel="noreferrer"
             className="flex items-center gap-1 text-emerald-600 hover:underline">
            <MapPin className="h-3 w-3" /> {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}
          </a>
        )}
        {item.notes && (
          <div className="text-slate-400 italic truncate" title={item.notes}>{item.notes}</div>
        )}
      </div>
    </div>
  );
}

export default function ProjectEvidenceTimeline() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    area_id: "",
    engineer: "",
    kinds: [],
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to)   params.set("date_to", filters.date_to);
    if (filters.area_id)   params.set("area_id", filters.area_id);
    if (filters.engineer)  params.set("engineer", filters.engineer);
    if (filters.kinds.length) params.set("kinds", filters.kinds.join(","));
    params.set("limit", "500");
    return params.toString();
  }, [filters]);

  // Load areas once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/areas?project_id=${projectId}`);
        if (alive) setAreas(r.data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Load timeline whenever filters change
  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pec/${projectId}/evidence-timeline?${queryString}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("تعذّر تحميل الـ Evidence Timeline");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, queryString]);

  const toggleKind = (k) => {
    setFilters(f => ({
      ...f,
      kinds: f.kinds.includes(k) ? f.kinds.filter(x => x !== k) : [...f.kinds, k],
    }));
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axiosInstance.get(`/pec/${projectId}/evidence-pack.pdf?${queryString}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Evidence_Pack_${projectId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم توليد Evidence Pack PDF");
    } catch (e) {
      toast.error("فشل توليد الـ PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="evidence-timeline-page" className="space-y-5 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => navigate(`/project-execution/${projectId}`)}
                  className="mb-2 text-[11px] text-violet-600 hover:underline">
            ‹ العودة لـ PEC
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-violet-600" />
            <h1 className="text-2xl font-bold text-slate-900">Evidence Timeline</h1>
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-700">
              Legal Proof Engine
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            سجل زمني قانوني لكل صورة مرفقة بـ MAR / MIR / السجل اليومي — مع GPS، Timestamps، والمصدر الكامل لكل دليل.
          </p>
        </div>
        <button
          data-testid="ev-export-pdf"
          onClick={exportPdf}
          disabled={exporting || !data?.count}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-500/30 hover:scale-[1.02] disabled:opacity-60"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "جارٍ التوليد…" : "Generate Evidence Pack PDF"}
        </button>
      </div>

      {/* Summary strip */}
      {data && (
        <div data-testid="ev-summary" className="grid gap-3 grid-cols-2 md:grid-cols-6">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase text-slate-500">إجمالي الأدلة</div>
            <div className="text-2xl font-bold text-slate-900">{fmt(data.total_unfiltered)}</div>
          </div>
          {KIND_DEFS.map(k => (
            <div key={k.key} className={`rounded-xl border border-${k.color}-200 bg-${k.color}-50 p-3`}>
              <div className={`text-[10px] uppercase text-${k.color}-700`}>{k.label}</div>
              <div className={`text-2xl font-bold text-${k.color}-700`}>{fmt(data.by_kind?.[k.key] || 0)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div data-testid="ev-filters" className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
          <Filter className="h-4 w-4 text-violet-600" /> فلاتر
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-700">من تاريخ</label>
            <input data-testid="ev-filter-from" type="date" value={filters.date_from}
                   onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">إلى تاريخ</label>
            <input data-testid="ev-filter-to" type="date" value={filters.date_to}
                   onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">المنطقة</label>
            <select data-testid="ev-filter-area" value={filters.area_id}
                    onChange={e => setFilters(f => ({ ...f, area_id: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">— كل المناطق —</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name} · {a.code}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">المهندس / الاستشاري</label>
            <input data-testid="ev-filter-engineer" value={filters.engineer}
                   onChange={e => setFilters(f => ({ ...f, engineer: e.target.value }))}
                   placeholder="اسم"
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-1 text-[10px] text-slate-400">(يُطبَّق على السجل اليومي فقط)</div>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] font-semibold text-slate-700">النوع</label>
          <div data-testid="ev-filter-kinds" className="mt-1 flex flex-wrap gap-2">
            {KIND_DEFS.map(k => {
              const active = filters.kinds.includes(k.key);
              return (
                <button key={k.key} data-testid={`ev-kind-${k.key}`}
                        onClick={() => toggleKind(k.key)}
                        className={`min-h-[36px] rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                          active ? `bg-${k.color}-600 text-white` : `bg-${k.color}-100 text-${k.color}-700 hover:bg-${k.color}-200`
                        }`}>
                  {k.label} {active && "✓"}
                </button>
              );
            })}
            {filters.kinds.length > 0 && (
              <button data-testid="ev-kinds-clear"
                      onClick={() => setFilters(f => ({ ...f, kinds: [] }))}
                      className="min-h-[36px] rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                مسح الفلاتر
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div data-testid="ev-loading" className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-violet-500" />
          <div className="mt-2 text-sm text-slate-500">جارٍ تحميل الـ Evidence…</div>
        </div>
      ) : !data?.by_day?.length ? (
        <div data-testid="ev-empty" className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <Camera className="mx-auto h-10 w-10 text-slate-400" />
          <div className="mt-3 text-base font-bold text-slate-700">لا توجد أدلة بعد</div>
          <p className="mt-1 text-sm text-slate-500">
            ارفع صوراً عبر الـ Daily Log / MAR / MIR لتظهر هنا بترتيب زمني قانوني.
          </p>
          <button onClick={() => navigate(`/project-execution/${projectId}`)}
                  className="mt-4 inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white">
            افتح PEC <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div data-testid="ev-days" className="space-y-6">
          {data.by_day.map(day => (
            <section key={day.day} data-testid={`ev-day-${day.day}`} className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-bold text-white">
                  <Calendar className="h-4 w-4" /> {day.day}
                </div>
                <div className="text-xs text-slate-500">{day.items.length} دليل</div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {day.items.map((it, idx) => (
                  <EvidenceCard key={`${it.kind}-${it.source_id}-${it.url}-${idx}`} item={it} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
